import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { registerWidget } from '../widgetRegistry';
import { useLayoutCtx } from '../LayoutContext';
import { authHeaders } from '../../CaseClient';

// Granular Add-Case widget — surface a "we already have N similar cases"
// hint above the intake form so workers don't double-create. Searches the
// existing /cases endpoint as the worker types the title (300ms debounce)
// and shows the count + the top 3 titles.
//
// v1 search criterion: case_type_id match + case-insensitive substring of
// the trimmed title (sent as the `search` query param the cases-list
// already understands). When the title is < 4 chars the widget is silent
// — matches against random short tokens are noise.

function AddCaseRelatedHint({ config }) {
  const { token, workspaceId, draft } = useLayoutCtx();
  const picked = draft?.picked || null;
  const title = (draft?.form?.title || '').trim();
  const minChars = Math.max(2, config.min_chars || 4);
  const limit = Math.max(1, Math.min(20, config.max_results || 5));
  const [matches, setMatches] = useState(null); // null=idle, []=none

  useEffect(() => {
    if (!picked?.id || !workspaceId || title.length < minChars) {
      setMatches(null);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(
          `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases`,
          {
            headers: authHeaders(token),
            params: {
              case_type_id: picked.id,
              search: title,
              page_size: limit,
              status: 'open,in_progress,under_review',
            },
          },
        );
        if (cancelled) return;
        const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        setMatches(items);
      } catch {
        if (!cancelled) setMatches([]);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [token, workspaceId, picked?.id, title, minChars, limit]);

  if (!matches || matches.length === 0) return null;

  return (
    <div style={{
      background: '#f59e0b15',
      border: '1px solid #f59e0b40',
      borderRadius: '6px',
      padding: '10px 12px',
      fontSize: '12px',
      marginBottom: '8px',
      color: 'var(--text-primary)',
    }}>
      <div style={{ fontSize: '11px', fontWeight: 600, color: '#f59e0b', marginBottom: '4px' }}>
        ⚠ {matches.length} similar open case{matches.length === 1 ? '' : 's'} found
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', color: 'var(--text-secondary)' }}>
        {matches.slice(0, 3).map(c => (
          <div key={c.id} style={{ display: 'flex', gap: '8px', fontSize: '12px' }}>
            <span style={{ fontFamily: 'monospace', color: 'var(--text-tertiary)' }}>{c.case_number || (c.id || '').slice(0, 8)}</span>
            <span>{c.title || '(untitled)'}</span>
          </div>
        ))}
        {matches.length > 3 && (
          <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '2px' }}>
            … and {matches.length - 3} more
          </div>
        )}
      </div>
    </div>
  );
}

registerWidget({
  type: 'add_case.related_hint',
  categories: ['add_case'],
  palette_label: 'Related cases hint',
  description: 'As the worker types the title, surfaces a "N similar open cases" warning to prevent duplicates. Debounced 300ms, only triggers above the configured title length.',
  Component: AddCaseRelatedHint,
  configSchema: {
    fields: [
      { type: 'number', name: 'min_chars', label: 'Minimum title length to search', default: 4, min: 2, max: 50 },
      { type: 'number', name: 'max_results', label: 'Max similar cases to look up', default: 5, min: 1, max: 20 },
    ],
  },
  publishes: [],
  subscribes: [],
  group: 'Add Case',
});
